// utils/codePositionUtils.js
/**
 * Utilities for managing code positions during nesting and reordering operations
 */

// Import normalizeId if it's available in your project
// If this import causes issues, you can implement a simpler version inline
import { normalizeId } from '../hooks/utils/idNormalization';

/**
 * Generic function to update positions of codes in a specific parent
 * Shifts positions for codes at or after the position threshold
 * 
 * @param {Array} codes - Array of code objects
 * @param {string|number|null} parentId - Parent ID to target (null for root level codes)
 * @param {number} positionThreshold - Position from which to start shifting
 * @param {number} delta - Amount to shift positions by (+1 to make room, -1 to close gaps)
 * @returns {Array} Updated array of codes with adjusted positions
 */
export function updatePositionsInParent(codes, parentId, positionThreshold, delta) {
  return codes.map(code => {
    // Only affect codes in the specified parent
    if ((code.parent_id || null) !== parentId) {
        return code
    };

    // Only affect codes at or after the threshold position
    if (code.position < positionThreshold) return code;

    return {
      ...code,
      position: (code.position || 1) + delta
    };
  });
}

/**
 * Updates code positions when removing a code from its current parent
 * Shifts down positions of sibling codes that come after the removed code
 * 
 * @param {Array} codes - Array of code objects
 * @param {string|number|null} parentId - Parent ID where code was removed from
 * @param {number} codePosition - Position of the removed code
 * @returns {Array} Updated array of codes with adjusted positions
 */
export function removeCodeFromParent(codes, parentId, codePosition) {
  return updatePositionsInParent(codes, parentId, codePosition, -1);
}

/**
 * Updates code positions when adding a code to a new parent
 * Shifts up positions of sibling codes at or after the insertion point
 * 
 * @param {Array} codes - Array of code objects
 * @param {string|number|null} parentId - Parent ID where code is being added
 * @param {number} insertPosition - Position where code will be inserted
 * @returns {Array} Updated array of codes with adjusted positions
 */
export function makeRoomInParent(codes, parentId, insertPosition) {
  return updatePositionsInParent(codes, parentId, insertPosition, 1);
}

/**
 * Updates the moved code with its new parent and position
 * 
 * @param {Array} codes - Array of code objects
 * @param {string|number} codeId - ID of the code being moved
 * @param {string|number|null} newParentId - New parent ID (null for root level)
 * @param {number} newPosition - New position of the code
 * @returns {Array} Updated array of codes with the moved code updated
 */
export function updateMovedCode(codes, codeId, newParentId, newPosition) {
  return codes.map(code => 
    normalizeId(code.id) === normalizeId(codeId)
      ? { ...code, parent_id: newParentId, position: newPosition }
      : code
  );
}
